import { useSelector, RootStateOrAny } from "react-redux";
import LocalStorageService from "../../services/LocalStorageService";
import { PhoneMasks, PhoneMask } from "../interfaces/PhoneMask";
import { Config } from "../interfaces/Config";

type GetPhoneMask = (phoneMask: PhoneMasks) => PhoneMask;
type CleanStringNumber = (maskedNumber?: string) => string;
type CleanFullPhoneNumber = (maskedFullPhoneNumber: string) => string;
type GetPhoneToDisplay = (phoneNumber: string) => string;
type GetPhoneToDisplayFn = (mask: string) => GetPhoneToDisplay;
type GetCountryCodeToDisplay = (countryCode: string) => string;
type GetFullPhoneToDisplay = (countryCode: string, phoneNumber: string) => string;
type UsePhoneMask = () => {
  phoneMask: PhoneMask;
  cleanStringNumber: CleanStringNumber;
  cleanFullPhoneNumber: CleanFullPhoneNumber;
  getCountryCodeToDisplay: GetCountryCodeToDisplay;
  getPhoneToDisplay: GetPhoneToDisplay;
  getFullPhoneToDisplay: GetFullPhoneToDisplay;
};

// Получение объекта, содержащего маски: кода страны, номер, код страны + " " + номер.
const getPhoneMask: GetPhoneMask = (phoneMasks) => {
  const tenantId = LocalStorageService.getTenantId();
  if (tenantId && tenantId in phoneMasks) {
    const { country, phone } = phoneMasks[tenantId];
    return { country, phone, full: `${country} ${phone}` };
  }
  const { country, phone } = phoneMasks.default;
  return { country, phone, full: `${country} ${phone}` };
};

// Удаление из строки всех нечисловых символов
const cleanStringNumber: CleanStringNumber = (maskedNumber) =>
  maskedNumber?.replace(/\D/g, "") || "";

/* Преобразование номера вида "+7 (111) 111-11-11" в "1111111111". Примечание: маска ввода формируется в getPhoneMask
  путем конкатенации кода страны, пробела и кода телефона, взятых из mainConfig.json, т.е. пробел после кода
  страны будет всегда, что и используется в данной формуле для разделения.
*/
const cleanFullPhoneNumber: CleanFullPhoneNumber = (maskedFullPhoneNumber) => {
  const phoneWithoutCounrtyCode = maskedFullPhoneNumber.split(" ").slice(1).join("");
  return cleanStringNumber(phoneWithoutCounrtyCode);
};

// Получение функции для стилизация телефонного номера (!без кода страны) в соответствии с маской из mainConfig.json
const getPhoneToDisplayFn: GetPhoneToDisplayFn = (mask) => (phoneNumber) => {
  const cleanedPhone = cleanStringNumber(phoneNumber);
  let result = "";
  for (let phonePos = 0, maskPos = 0; phonePos < cleanedPhone.length; ) {
    const isNumberMaskSign = /\d/.test(mask[maskPos]);
    if (isNumberMaskSign) {
      result += cleanedPhone[phonePos];
      phonePos += 1;
    } else if (maskPos > mask.length - 1) {
      result += cleanedPhone.slice(phonePos);
      break;
    } else {
      result += mask[maskPos];
    }
    maskPos += 1;
  }
  return result;
};

// Преобразование кода страны: "7" || "+7" => "+7", "" => ""
const getCountryCodeToDisplay: GetCountryCodeToDisplay = (countryCode) => {
  const cleanedCountryCode = cleanStringNumber(countryCode);
  if (cleanedCountryCode) {
    return `+${cleanedCountryCode}`;
  }
  return "";
};

const usePhoneMask: UsePhoneMask = () => {
  const { config }: { config: Config } = useSelector((state: RootStateOrAny) => state.config);

  const phoneMask = getPhoneMask(config.phoneMasks);

  const getPhoneToDisplay = getPhoneToDisplayFn(phoneMask.phone);

  const getFullPhoneToDisplay: GetFullPhoneToDisplay = (countryCode, phone) =>
    `${getCountryCodeToDisplay(countryCode)} ${getPhoneToDisplay(phone)}`;

  return {
    phoneMask,
    cleanStringNumber,
    cleanFullPhoneNumber,
    getCountryCodeToDisplay,
    getPhoneToDisplay,
    getFullPhoneToDisplay,
  };
};

export default usePhoneMask;
